import React, { useState, useEffect } from 'react';
import { Dimensions } from 'react-native';
import { OverlayViewF, OVERLAY_MOUSE_TARGET } from '@react-google-maps/api';
import { useAuth } from '../services/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { isClubMember, getClubMemberCount, getUserById, getFriendshipStatus, sendFriendRequest, respondToFriendRequest, removeFriend, cancelFriendRequest, joinClub, leaveClub, listEvents, createRsvp, updateUser as updateUserSupabase } from '../services/database';
import NotificationService from '../services/NotificationService';
import { getProfilePictureUrl } from '../services/storage';
import { useTheme } from '../contexts/ThemeContext';
import { getColors } from '../styles/theme';
import PaymentModal from './PaymentModal';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');
const isMobile = SCREEN_WIDTH < 768;
const MOBILE_NAV_BAR_HEIGHT = 85;
const MOBILE_POPUP_VERTICAL_SPACING = 48;
const MOBILE_MAX_POPUP_HEIGHT_AVAILABLE = SCREEN_HEIGHT - MOBILE_NAV_BAR_HEIGHT - MOBILE_POPUP_VERTICAL_SPACING;
// Match HotspotPopup dimensions: same max height on mobile; slightly narrower width for breathing room from screen edges
const POPUP_WIDTH = isMobile ? Math.min(300, SCREEN_WIDTH * 0.86) : Math.min(380, SCREEN_WIDTH * 0.9);
const POPUP_MAX_HEIGHT = isMobile ? Math.max(320, MOBILE_MAX_POPUP_HEIGHT_AVAILABLE) : Math.min(480, SCREEN_HEIGHT * 0.65);

const UserProfilePopup = ({ visible, userId, userCoordinate, onClose, onSendMessage, navigation, initialUserData, isMapDragging, frozenPopupViewport }) => {
  const { user, appUser } = useAuth();
  const { showToast } = useToast();
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [currentUserDbId, setCurrentUserDbId] = useState(null);
  const [rsvpingEventId, setRsvpingEventId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserAccountType, setCurrentUserAccountType] = useState(null);
  const [isMember, setIsMember] = useState(false);
  const [memberCount, setMemberCount] = useState(0);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedEventForPayment, setSelectedEventForPayment] = useState(null);
  const [showFullProfileModal, setShowFullProfileModal] = useState(false);
  const [friendshipStatus, setFriendshipStatus] = useState({ status: 'NONE' });
  const [friendActionLoading, setFriendActionLoading] = useState(false);
  const [membershipLoading, setMembershipLoading] = useState(false);

  useEffect(() => {
    if (visible && userId) {
      fetchUserData();
    }
  }, [visible, userId]);

  // Keep in sync with auth — popup only runs fetchUserData on open; appUser often loads after, so we must update when it arrives
  useEffect(() => {
    if (appUser?.id) {
      setCurrentUserDbId(appUser.id);
      setIsAdmin(appUser.isAdmin || false);
      setCurrentUserAccountType(appUser.accountType);
    } else {
      setCurrentUserDbId(null);
      setIsAdmin(false);
      setCurrentUserAccountType(null);
    }
  }, [appUser]);

  // Check membership status for clubs
  useEffect(() => {
    const checkMembership = async () => {
      if (visible && userId && currentUserDbId && userData?.accountType === 'CLUB') {
        try {
          const [memberStatus, count] = await Promise.all([
            isClubMember(userId, currentUserDbId),
            getClubMemberCount(userId),
          ]);
          setIsMember(memberStatus);
          setMemberCount(count);
        } catch (error) {
          console.error('Error checking membership:', error);
        }
      }
    };
    checkMembership();
  }, [visible, userId, currentUserDbId, userData?.accountType]);

  // Load profile image URL when userData changes
  useEffect(() => {
    const loadProfileImage = async () => {
      if (userData?.profilePictureUrl) {
        try {
          // Use Supabase storage helper function
          const url = getProfilePictureUrl(userData.profilePictureUrl, 'medium');
          setProfileImageUrl(url);
        } catch (error) {
          console.error('Error loading profile image:', error);
          setProfileImageUrl(null);
        }
      } else {
        setProfileImageUrl(null);
      }
    };
    loadProfileImage();
  }, [userData]);

  // Fetch friendship status when full-profile modal is open (players only)
  useEffect(() => {
    const check = async () => {
      const me = appUser?.id ?? currentUserDbId;
      if (showFullProfileModal && userId && me && userData?.accountType === 'PLAYER' && me !== userId) {
        try {
          const status = await getFriendshipStatus(me, userId);
          setFriendshipStatus(status);
        } catch (error) {
          console.error('Error checking friendship status:', error);
        }
      }
    };
    check();
  }, [showFullProfileModal, userId, currentUserDbId, userData?.accountType, appUser?.id]);

  const checkFriendshipStatus = async () => {
    const me = appUser?.id ?? currentUserDbId;
    if (!me || !userId) return;
    try {
      const status = await getFriendshipStatus(me, userId);
      setFriendshipStatus(status);
    } catch (error) {
      console.error('Error checking friendship status:', error);
    }
  };

  const handleAddFriend = async () => {
    const senderId = appUser?.id || currentUserDbId;
    if (!senderId) {
      showToast('Please log in to add friends', 'error');
      return;
    }
    setFriendActionLoading(true);
    try {
      const result = await sendFriendRequest(senderId, userId);
      if (result?.status === 'PENDING' && result?.receiver_id === userId) {
        NotificationService.notifyFriendRequest(userId, appUser?.username || appUser?.clubName || 'Someone');
      }
      if (result?.status === 'ACCEPTED') {
        showToast(`You are now friends with ${userData?.username}!`, 'success');
      } else {
        showToast('Friend request sent!', 'success');
      }
      checkFriendshipStatus();
    } catch (error) {
      console.error('Error sending friend request:', error);
      showToast(error?.message || 'Failed to send friend request', 'error');
    } finally {
      setFriendActionLoading(false);
    }
  };

  const handleAcceptFriend = async () => {
    setFriendActionLoading(true);
    try {
      await respondToFriendRequest(friendshipStatus.requestId, 'ACCEPTED');
      showToast(`You are now friends with ${userData?.username}!`, 'success');
      checkFriendshipStatus();
    } catch (error) {
      console.error('Error accepting friend request:', error);
      showToast('Failed to accept request', 'error');
    } finally {
      setFriendActionLoading(false);
    }
  };

  const handleCancelRequest = async () => {
    setFriendActionLoading(true);
    try {
      await cancelFriendRequest(friendshipStatus.requestId);
      showToast('Friend request cancelled', 'info');
      checkFriendshipStatus();
    } catch (error) {
      console.error('Error cancelling friend request:', error);
      showToast('Failed to cancel request', 'error');
    } finally {
      setFriendActionLoading(false);
    }
  };

  const handleRemoveFriend = async () => {
    const me = appUser?.id ?? currentUserDbId;
    if (!me) return;
    if (!window.confirm(`Remove ${userData?.username} from friends?`)) return;
    setFriendActionLoading(true);
    try {
      await removeFriend(me, userId);
      showToast(`Removed ${userData?.username} from friends`, 'info');
      checkFriendshipStatus();
    } catch (error) {
      console.error('Error removing friend:', error);
      showToast('Failed to remove friend', 'error');
    } finally {
      setFriendActionLoading(false);
    }
  };

  const handleJoinClub = async () => {
    if (!currentUserDbId) {
      showToast('Please log in to join clubs', 'error');
      return;
    }
    setMembershipLoading(true);
    try {
      await joinClub(userId, currentUserDbId);
      setIsMember(true);
      setMemberCount(prev => prev + 1);
      showToast('Successfully joined the club!', 'success');
    } catch (error) {
      console.error('Error joining club:', error);
      if (error.message?.includes('Already a member') || error.message?.includes('already')) {
        showToast('You are already a member of this club', 'info');
        setIsMember(true);
      } else if (error.message?.includes('own club')) {
        showToast('You cannot join your own club', 'info');
      } else {
        showToast('Failed to join club', 'error');
      }
    } finally {
      setMembershipLoading(false);
    }
  };

  const handleLeaveClub = async () => {
    if (!window.confirm('Are you sure you want to leave this club?')) return;
    setMembershipLoading(true);
    try {
      await leaveClub(userId, currentUserDbId);
      setIsMember(false);
      setMemberCount(prev => Math.max(0, prev - 1));
      showToast('You have left the club', 'info');
    } catch (error) {
      console.error('Error leaving club:', error);
      showToast('Failed to leave club', 'error');
    } finally {
      setMembershipLoading(false);
    }
  };

  const fetchUserData = async () => {
    try {
      setLoading(true);
      
      // If initialUserData is provided, use it (from MapScreen)
      if (initialUserData) {
        console.log('✅ Using initialUserData from MapScreen:', initialUserData);
        setUserData(initialUserData);
        if (initialUserData.accountType === 'CLUB') {
          fetchClubEvents(userId);
        }
        setLoading(false);
        return;
      }
      
      // Otherwise, fetch from Supabase
      const dbUser = await getUserById(userId);
      
      if (!dbUser) {
        console.error('User not found:', userId);
        setLoading(false);
        return;
      }
      
      console.log('✅ Fetched user from Supabase:', dbUser);
      
      // Convert snake_case to camelCase
      const user = {
        id: dbUser.id,
        username: dbUser.username,
        clubName: dbUser.club_name,
        accountType: dbUser.account_type,
        profilePictureUrl: dbUser.profile_picture_url,
        rating: dbUser.rating || dbUser.in_person_rating || 1200,
        chesscomUsername: dbUser.chesscom_username,
        lichessUsername: dbUser.lichess_username,
        gamesPlayedInApp: dbUser.games_played_in_app,
        about: dbUser.about,
        address: dbUser.address,
        website: dbUser.website,
        claimedBy: dbUser.claimed_by,
        city: dbUser.city,
        statusMessage: dbUser.status_message,
        isOnline: dbUser.is_online,
        lastOnlineAt: dbUser.last_online_at,
      };
      
      console.log('✅ Converted to camelCase:', user);
      setUserData(user);

      // Fetch events if this is a club
      if (user?.accountType === 'CLUB') {
        fetchClubEvents(userId);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClubEvents = async (clubId) => {
    try {
      const allEvents = await listEvents({ clubId, includeRsvps: true });

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const events = (allEvents || []).filter((event) => {
        const endDate = event.end_time ? new Date(event.end_time) : new Date(new Date(event.event_date).getTime() + 4 * 3600000);
        return endDate >= today;
      });

      // Take only next 3 events and check if user has already RSVP'd
      const eventsWithDetails = events.slice(0, 3).map(event => {
        const rsvpList = event.rsvps || [];
        const userHasRSVP = currentUserDbId && rsvpList.some(
          rsvp => rsvp.user_id === currentUserDbId
        );
        return {
          ...event,
          // Map snake_case to camelCase for template compatibility
          eventDate: event.event_date,
          eventTime: event.event_time,
          eventType: event.event_type,
          isPaid: event.is_paid,
          stripeFeePercentage: event.stripe_fee_percentage,
          userHasRSVP,
          club: event.club || userData,
        };
      });
      setUpcomingEvents(eventsWithDetails);
    } catch (error) {
      console.error('Error fetching club events:', error);
      setUpcomingEvents([]);
    }
  };

  // Refetch events when currentUserDbId changes to update RSVP status
  useEffect(() => {
    if (currentUserDbId && userData?.accountType === 'CLUB') {
      fetchClubEvents(userId);
    }
  }, [currentUserDbId]);

  const handlePaymentSuccess = async (paymentIntentId, amountPaid) => {
    // Payment was successful - RSVP should be created by webhook
    // But we'll refresh events to show updated status
    if (userData?.accountType === 'CLUB') {
      await fetchClubEvents(userId);
    }
    setShowPaymentModal(false);
    setSelectedEventForPayment(null);
    showToast('Payment successful! You are registered for the event.', 'success');
  };

  const handleRSVP = async (event) => {
    if (!currentUserDbId) {
      showToast('Please log in to RSVP', 'error');
      return;
    }

    // Check if already registered
    if (event.userHasRSVP) {
      showToast('You have already registered for this event', 'info');
      return;
    }

    // Check if event is paid
    if (event.isPaid) {
      // Check if club has Stripe account set up
      const club = event.club || userData; // Fallback to userData
      if (!club?.stripeAccountId || !club?.stripeOnboardingComplete) {
        showToast('This club has not set up payments yet. Please contact the club.', 'error');
        return;
      }

      // Show payment modal
      setSelectedEventForPayment({
        ...event,
        club: club, // Ensure club data is included
      });
      setShowPaymentModal(true);
      return;
    }

    // Handle free event RSVP
    setRsvpingEventId(event.id);
    try {
      await createRsvp({
        event_id: event.id,
        user_id: currentUserDbId,
        has_paid: false,
        amount_paid: null,
        payment_intent_id: null,
      });

      showToast('Successfully registered for event!', 'success');
      
      // Refresh events to show updated status
      if (userData?.accountType === 'CLUB') {
        fetchClubEvents(userId);
      }
    } catch (error) {
      console.error('Error creating RSVP:', error);
      if (error.errors && error.errors[0]?.message.includes('already exists')) {
        showToast('You have already registered for this event', 'info');
        // Refresh to update UI
        if (userData?.accountType === 'CLUB') {
          fetchClubEvents(userId);
        }
      } else {
        showToast('Failed to register. Please try again.', 'error');
      }
    } finally {
      setRsvpingEventId(null);
    }
  };

  const displayName = userData?.accountType === 'CLUB' ? userData?.clubName : userData?.username;
  console.log('🔍 UserProfilePopup render - userData:', userData, 'displayName:', displayName);
  const profilePic = profileImageUrl || 
    `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName || 'User')}&size=300`;

  if (!visible || !userCoordinate) return null;

  const renderFriendButton = () => {
    const me = appUser?.id ?? currentUserDbId;
    if (me === userId || userData?.accountType === 'CLUB') return null;
    const handlePress = () => {
      if (friendActionLoading) return;
      switch (friendshipStatus.status) {
        case 'ACCEPTED': handleRemoveFriend(); break;
        case 'PENDING': friendshipStatus.isSender ? handleCancelRequest() : handleAcceptFriend(); break;
        default: handleAddFriend(); break;
      }
    };
    const label = friendshipStatus.status === 'ACCEPTED' ? 'Friends' :
      friendshipStatus.status === 'PENDING' && friendshipStatus.isSender ? 'Request Pending' :
      friendshipStatus.status === 'PENDING' ? 'Accept Request' : 'Add Friend';
    const isPrimary = friendshipStatus.status === 'PENDING' && !friendshipStatus.isSender;
    return (
      <button
        type="button"
        onClick={handlePress}
        disabled={friendActionLoading}
        style={{
          width: '100%',
          padding: '10px 16px',
          marginTop: '8px',
          borderRadius: '10px',
          border: friendshipStatus.status === 'ACCEPTED' ? `2px solid ${colors.primary}` : 'none',
          backgroundColor: friendshipStatus.status === 'ACCEPTED' ? 'transparent' : friendshipStatus.status === 'PENDING' && friendshipStatus.isSender ? colors.backgroundDark : colors.primary,
          color: friendshipStatus.status === 'ACCEPTED' ? (isDarkMode ? '#81C784' : '#2E7D32') : friendshipStatus.status === 'PENDING' && friendshipStatus.isSender ? colors.textSecondary : 'white',
          fontSize: '13px',
          fontWeight: '600',
          cursor: friendActionLoading ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
        }}
      >
        {friendActionLoading ? '...' : <><span>👤</span>{label}</>}
      </button>
    );
  };

  const profilePopupContent = (
      <div
        style={{
          position: 'absolute',
          animation: 'popupFadeIn 0.3s ease-out',
          transformOrigin: isMobile ? 'bottom center' : 'bottom center',
          zIndex: 100000,
          // On mobile: position above marker (same as HotspotPopup)
          ...(isMobile
            ? { bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: '15px' }
            : {}),
        }}
      >
        <div
          className="map-popup-content"
          style={{
            backgroundColor: colors.surface,
            borderRadius: '20px',
            padding: isMobile ? 12 : 20,
            width: `${POPUP_WIDTH}px`,
            maxWidth: '92vw',
            maxHeight: `${POPUP_MAX_HEIGHT}px`,
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            boxShadow: isDarkMode ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(0,0,0,0.15)',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button - high z-index so it stays clickable above content */}
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              backgroundColor: colors.backgroundDark,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              color: colors.textSecondary,
              transition: 'background-color 0.2s',
              zIndex: 100002,
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.border}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.backgroundDark}
          >
            ×
          </button>

          {loading ? (
            <div style={{ padding: isMobile ? '24px' : '40px', textAlign: 'center' }}>
              <div style={{ 
                border: `3px solid ${colors.backgroundDark}`,
                borderTop: `3px solid ${colors.primary}`,
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                animation: 'spin 1s linear infinite',
                margin: '0 auto',
              }} />
            </div>
          ) : (
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch' }}>
              {/* Profile Picture - Compact */}
              <div style={{ textAlign: 'center', marginBottom: isMobile ? 8 : 12, position: 'relative' }}>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img 
                    src={profilePic} 
                    alt={displayName}
                    style={{
                      width: isMobile ? 72 : 100,
                      height: isMobile ? 72 : 100,
                      borderRadius: '50%',
                      border: `4px solid ${colors.surface}`,
                      boxShadow: isDarkMode ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.15)',
                      objectFit: 'cover',
                    }}
                  />
                  {/* Online Status Indicator */}
                  <div style={{
                    position: 'absolute',
                    bottom: '8px',
                    right: '8px',
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    backgroundColor: userData?.isOnline ? '#4CAF50' : '#9E9E9E',
                    border: `3px solid ${colors.surface}`,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }} />
                </div>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '6px 12px',
                  borderRadius: '16px',
                  backgroundColor: userData?.accountType === 'CLUB' ? '#76AF87' : colors.primary,
                  marginTop: '-16px',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}>
                  {userData?.accountType === 'CLUB' ? '🏛️ Club' : '♟️ Player'}
                </div>
              </div>

              {/* Live Today Badge for Clubs */}
              {userData?.accountType === 'CLUB' && upcomingEvents.length > 0 && (() => {
                const now = new Date();
                const todayStart = new Date(now); todayStart.setHours(0,0,0,0);
                const todayEnd = new Date(now); todayEnd.setHours(23,59,59,999);
                const liveEvent = upcomingEvents.find(e => {
                  const d = new Date(e.eventDate);
                  return d >= todayStart && d <= todayEnd;
                });
                if (!liveEvent) return null;
                const timeStr = liveEvent.eventTime ? (() => {
                  const [h, m] = liveEvent.eventTime.split(':');
                  const hour = parseInt(h); const ampm = hour >= 12 ? 'pm' : 'am';
                  const dh = hour % 12 || 12;
                  return `${dh}${m !== '00' ? `:${m}` : ''}${ampm}`;
                })() : '';
                return (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    backgroundColor: '#E8F5E9',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    borderLeft: '3px solid #4CAF50',
                  }}>
                    <div style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#4CAF50' }} />
                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#2E7D32' }}>
                      Live Today: {liveEvent.title}{timeStr ? ` – ${timeStr}` : ''}
                    </span>
                  </div>
                );
              })()}

              {/* Membership Badge for Clubs */}
              {userData?.accountType === 'CLUB' && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px',
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '12px',
                    color: colors.textSecondary,
                  }}>
                    <span>👥</span>
                    <span>{memberCount} {memberCount === 1 ? 'member' : 'members'}</span>
                  </div>
                  {isMember && (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      backgroundColor: isDarkMode ? 'rgba(127, 176, 105, 0.2)' : '#E8F5E9',
                      fontSize: '11px',
                      fontWeight: '600',
                      color: isDarkMode ? '#81C784' : '#4CAF50',
                      gap: '4px',
                    }}>
                      <span>✓</span>
                      <span>Member</span>
                    </div>
                  )}
                </div>
              )}

              {/* Name */}
              <div style={{
                fontSize: isMobile ? 17 : 20,
                fontWeight: 'bold',
                textAlign: 'center',
                color: colors.text,
                marginBottom: '4px',
              }}>{displayName}</div>

              {/* City */}
              {userData?.city && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  marginBottom: '6px',
                }}>
                  <span style={{ fontSize: '12px' }}>📍</span>
                  <span style={{ fontSize: '12px', color: colors.textSecondary }}>{userData.city}</span>
                </div>
              )}

              {/* Status Message */}
              {userData?.statusMessage && (
                <div style={{
                  fontSize: '12px',
                  color: colors.textSecondary,
                  textAlign: 'center',
                  marginBottom: '10px',
                  lineHeight: '1.4',
                  fontStyle: 'italic',
                  padding: '8px 12px',
                  backgroundColor: colors.backgroundDark,
                  borderRadius: '8px',
                  maxHeight: '48px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}>
                  "{userData.statusMessage}"
                </div>
              )}

              {/* Bio Section for Players - Compact */}
              {userData?.accountType === 'PLAYER' && userData?.about && (
                <div style={{
                  fontSize: '12px',
                  color: colors.textSecondary,
                  textAlign: 'center',
                  marginBottom: '10px',
                  lineHeight: '1.4',
                  fontStyle: 'italic',
                  padding: '0 8px',
                  maxHeight: '32px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}>{userData.about}</div>
              )}

              {/* Player Stats - Compact */}
              {userData?.accountType === 'PLAYER' && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: '12px', 
                  marginBottom: '12px',
                  padding: '10px',
                  backgroundColor: colors.backgroundDark,
                  borderRadius: '10px',
                }}>
                  {userData?.rating && (
                    <div style={{ textAlign: 'center', flex: 1 }}>
                      <div style={{ fontSize: '18px', marginBottom: '2px' }}>⭐</div>
                      <div style={{ fontSize: '16px', fontWeight: 'bold', color: colors.text }}>{userData.rating}</div>
                      <div style={{ fontSize: '9px', color: colors.textSecondary, marginTop: '1px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Rating</div>
                    </div>
                  )}
                  {userData?.gamesPlayedInApp !== undefined && (
                    <div style={{ textAlign: 'center', flex: 1 }}>
                      <div style={{ fontSize: '18px', marginBottom: '2px' }}>🎮</div>
                      <div style={{ fontSize: '16px', fontWeight: 'bold', color: colors.text }}>{userData.gamesPlayedInApp || 0}</div>
                      <div style={{ fontSize: '9px', color: colors.textSecondary, marginTop: '1px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Games</div>
                    </div>
                  )}
                </div>
              )}

              {/* Chess External Links for Players - Compact */}
              {userData?.accountType === 'PLAYER' && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {/* Chess.com Button */}
                    <a
                      href={userData?.chesscomUsername ? `https://chess.com/member/${userData.chesscomUsername}` : '#'}
                      target={userData?.chesscomUsername ? '_blank' : ''}
                      rel="noopener noreferrer"
                      onClick={(e) => !userData?.chesscomUsername && e.preventDefault()}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '8px',
                        borderRadius: '8px',
                        backgroundColor: userData?.chesscomUsername ? '#81b64c' : colors.backgroundDark,
                        color: userData?.chesscomUsername ? 'white' : colors.textSecondary,
                        fontSize: '11px',
                        fontWeight: '600',
                        textDecoration: 'none',
                        cursor: userData?.chesscomUsername ? 'pointer' : 'not-allowed',
                        opacity: userData?.chesscomUsername ? 1 : 0.6,
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        border: 'none',
                      }}
                      onMouseEnter={(e) => {
                        if (userData?.chesscomUsername) {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(129, 182, 76, 0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M2 2h9v9H2V2zm0 11h9v9H2v-9zm11-11h9v9h-9V2zm0 11h9v9h-9v-9z"/>
                      </svg>
                      Chess.com
                    </a>

                    {/* Lichess Button */}
                    <a
                      href={userData?.lichessUsername ? `https://lichess.org/@/${userData.lichessUsername}` : '#'}
                      target={userData?.lichessUsername ? '_blank' : ''}
                      rel="noopener noreferrer"
                      onClick={(e) => !userData?.lichessUsername && e.preventDefault()}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '8px',
                        borderRadius: '8px',
                        backgroundColor: userData?.lichessUsername ? '#000000' : colors.backgroundDark,
                        color: userData?.lichessUsername ? 'white' : colors.textSecondary,
                        fontSize: '11px',
                        fontWeight: '600',
                        textDecoration: 'none',
                        cursor: userData?.lichessUsername ? 'pointer' : 'not-allowed',
                        opacity: userData?.lichessUsername ? 1 : 0.6,
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        border: 'none',
                      }}
                      onMouseEnter={(e) => {
                        if (userData?.lichessUsername) {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                      </svg>
                      Lichess
                    </a>
                  </div>
                </div>
              )}

              {/* Club Info - Compact */}
              {userData?.accountType === 'CLUB' && (
                <>
                  {userData?.about && (
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ 
                        fontSize: '12px', 
                        color: colors.text, 
                        lineHeight: '1.4',
                        maxHeight: '34px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}>{userData.about}</div>
                    </div>
                  )}
                  {userData?.address && (
                    <div style={{ 
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '12px',
                      color: colors.text,
                      padding: '6px 10px',
                      backgroundColor: colors.backgroundDark,
                      borderRadius: '6px',
                    }}>
                      <span style={{ fontSize: '14px' }}>📍</span>
                      <span>{userData.address}</span>
                    </div>
                  )}
                  {userData?.website && (
                    <div style={{ marginBottom: '10px' }}>
                      <a
                        href={userData.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '12px',
                          color: colors.primary,
                          textDecoration: 'none',
                          padding: '6px 10px',
                          backgroundColor: colors.backgroundDark,
                          borderRadius: '6px',
                          transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = colors.border}
                        onMouseLeave={(e) => e.target.style.backgroundColor = colors.backgroundDark}
                      >
                        <span style={{ fontSize: '14px' }}>🌐</span>
                        <span style={{ fontWeight: '500' }}>Visit Website</span>
                      </a>
                    </div>
                  )}

                  {/* Next Upcoming Event - Top 1 Only */}
                  {upcomingEvents.length > 0 && (
                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${colors.border}` }}>
                      {(() => {
                        const nextEvent = upcomingEvents[0];
                        const eventDate = new Date(nextEvent.eventDate);
                        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                        const dayName = days[eventDate.getDay()];
                        const timeStr = nextEvent.eventTime 
                          ? (() => {
                              const [hours, minutes] = nextEvent.eventTime.split(':');
                              const hour = parseInt(hours);
                              const ampm = hour >= 12 ? 'pm' : 'am';
                              const displayHour = hour % 12 || 12;
                              return `${displayHour}${minutes !== '00' ? `:${minutes}` : ''}${ampm}`;
                            })()
                          : '';
                        
                        return (
                          <div key={nextEvent.id}>
                            <div
                              style={{
                                padding: '10px',
                                backgroundColor: colors.backgroundDark,
                                borderRadius: '8px',
                                borderLeft: `3px solid ${colors.primary}`,
                                marginBottom: '8px',
                              }}
                            >
                              <div style={{
                                fontSize: '13px',
                                fontWeight: '600',
                                color: colors.text,
                                marginBottom: '4px',
                              }}>
                                {nextEvent.title}
                              </div>
                              <div style={{
                                fontSize: '11px',
                                color: colors.textSecondary,
                              }}>
                                {dayName} {timeStr && `· ${timeStr}`}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button
                                onClick={() => {
                                  if (navigation) {
                                    navigation.navigate('EventDetails', { eventId: nextEvent.id });
                                    onClose();
                                  }
                                }}
                                style={{
                                  flex: 1,
                                  padding: '8px',
                                  backgroundColor: colors.primary,
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  color: colors.text,
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.primaryDark}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.primary}
                              >
                                View Event
                              </button>
                              <button
                                onClick={() => {
                                  if (navigation) {
                                    navigation.navigate('UserDetail', { userId: userId });
                                    onClose();
                                  }
                                }}
                                style={{
                                  flex: 1,
                                  padding: '8px',
                                  backgroundColor: colors.backgroundDark,
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  color: colors.text,
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.border}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.backgroundDark}
                              >
                                View Club
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  {/* Legacy events map - keeping for now but should be removed */}
                  {false && upcomingEvents.length > 0 && (
                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${colors.border}` }}>
                      <div style={{ 
                        fontSize: '11px', 
                        fontWeight: '600', 
                        color: colors.textSecondary, 
                        marginBottom: '8px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}>
                        📅 Upcoming Events
                      </div>
                      {upcomingEvents.map((event) => (
                        <div
                          key={event.id}
                          style={{
                            padding: '10px',
                            marginBottom: '8px',
                            backgroundColor: colors.backgroundDark,
                            borderRadius: '8px',
                            borderLeft: `3px solid ${colors.primary}`,
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '4px',
                          }}>
                            <div style={{
                              fontSize: '12px',
                              fontWeight: '600',
                              color: colors.text,
                              flex: 1,
                            }}>
                              {event.title}
                            </div>
                            {event.isPaid && event.price && (
                              <div style={{
                                backgroundColor: '#94EB98',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '10px',
                                fontWeight: '600',
                                color: colors.text,
                                marginLeft: '8px',
                              }}>
                                ${event.price}
                              </div>
                            )}
                            {!event.isPaid && (
                              <div style={{
                                backgroundColor: colors.backgroundDark,
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '10px',
                                fontWeight: '600',
                                color: colors.textSecondary,
                                marginLeft: '8px',
                              }}>
                                FREE
                              </div>
                            )}
                          </div>
                          <div style={{
                            fontSize: '10px',
                            color: colors.textSecondary,
                            marginBottom: '8px',
                          }}>
                            {new Date(event.eventDate).toLocaleDateString('en-US', { 
                              weekday: 'short', 
                              month: 'short', 
                              day: 'numeric' 
                            })} {event.eventTime && `• ${event.eventTime}`}
                          </div>
                          {currentUserDbId && (
                            <button
                              onClick={() => handleRSVP(event)}
                              disabled={rsvpingEventId === event.id || event.userHasRSVP}
                              style={{
                                width: '100%',
                                padding: '6px 12px',
                                backgroundColor: event.userHasRSVP ? (isDarkMode ? 'rgba(127, 176, 105, 0.2)' : '#e8f5e9') : rsvpingEventId === event.id ? colors.border : colors.primary,
                                border: event.userHasRSVP ? `2px solid ${colors.primary}` : 'none',
                                borderRadius: '6px',
                                fontSize: '11px',
                                fontWeight: '600',
                                color: colors.text,
                                cursor: (rsvpingEventId === event.id || event.userHasRSVP) ? 'not-allowed' : 'pointer',
                                transition: 'transform 0.2s, background-color 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                              }}
                              onMouseEnter={(e) => {
                                if (rsvpingEventId !== event.id && !event.userHasRSVP) {
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                  e.currentTarget.style.backgroundColor = colors.primaryDark;
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (rsvpingEventId !== event.id && !event.userHasRSVP) {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.backgroundColor = colors.primary;
                                }
                              }}
                            >
                              {event.userHasRSVP ? (
                                <>
                                  <span style={{ fontSize: '14px' }}>✓</span>
                                  <span>Registered</span>
                                </>
                              ) : rsvpingEventId === event.id ? (
                                'Registering...'
                              ) : event.isPaid ? (
                                `Register • $${event.price}`
                              ) : (
                                'Register (Free)'
                              )}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* View Full Profile Button - opens modal */}
              <button
                onClick={() => setShowFullProfileModal(true)}
                  style={{
                    backgroundColor: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '10px 16px',
                    borderRadius: '10px',
                    marginTop: '12px',
                    width: '100%',
                    border: `2px solid ${colors.primary}`,
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: colors.text,
                    gap: '6px',
                    transition: 'transform 0.2s, background-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(127, 176, 105, 0.15)' : '#e8f5e9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span style={{ fontSize: '16px' }}>👤</span>
                  View Full Profile
                </button>

              {/* Send Message Button - Compact */}
              <button
                onClick={() => {
                  onSendMessage(userData);
                  onClose();
                }}
                style={{
                  backgroundColor: colors.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '12px 20px',
                  borderRadius: '10px',
                  marginTop: '8px',
                  width: '100%',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'white',
                  gap: '8px',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  boxShadow: isDarkMode ? '0 2px 8px rgba(127, 176, 105, 0.3)' : '0 2px 8px rgba(148, 235, 152, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = isDarkMode ? '0 6px 16px rgba(127, 176, 105, 0.4)' : '0 6px 16px rgba(148, 235, 152, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = isDarkMode ? '0 2px 8px rgba(127, 176, 105, 0.3)' : '0 2px 8px rgba(148, 235, 152, 0.3)';
                }}
              >
                <span style={{ fontSize: '20px' }}>💬</span>
                Send Message
              </button>
            </div>
          )}
        </div>

        {/* Pointer/Arrow pointing down to marker */}
        <div style={{
          width: 0,
          height: 0,
          borderLeft: '12px solid transparent',
          borderRight: '12px solid transparent',
          borderTop: `12px solid ${colors.surface}`,
          position: 'absolute',
          bottom: '-12px',
          left: '50%',
          transform: 'translateX(-50%)',
          filter: isDarkMode ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' : 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
        }} />

        {/* CSS Keyframes for animation */}
        <style>{`
          @keyframes popupFadeIn {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
  );

  const renderProfilePopup = () => {
    const useFixedOverlayDrag = visible && isMapDragging && frozenPopupViewport && typeof document !== 'undefined';
    if (useFixedOverlayDrag) {
      const ReactDOM = require('react-dom');
      const topY = isMobile ? frozenPopupViewport.y - 15 : frozenPopupViewport.y;
      return ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            left: frozenPopupViewport.x,
            top: topY,
            transform: 'translate(-50%, -100%)',
            zIndex: 100000,
            pointerEvents: 'auto',
          }}
        >
          {profilePopupContent}
        </div>,
        document.body
      );
    }
    return (
      <OverlayViewF
        position={{ lat: userCoordinate.latitude, lng: userCoordinate.longitude }}
        mapPaneName={OVERLAY_MOUSE_TARGET}
        getPixelPositionOffset={() =>
          isMobile
            ? { x: 0, y: 0 }
            : { x: -POPUP_WIDTH / 2, y: -430 }
        }
      >
        {profilePopupContent}
      </OverlayViewF>
    );
  };

  return (
    <>
    {!showFullProfileModal && visible && userCoordinate && renderProfilePopup()}

    {/* Full Profile Modal */}
    {showFullProfileModal && userData && (
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 20000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          backgroundColor: isDarkMode ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)',
          animation: 'popupFadeIn 0.2s ease-out',
        }}
        onClick={() => setShowFullProfileModal(false)}
      >
        <div
          style={{
            backgroundColor: colors.surface,
            borderRadius: '20px',
            boxShadow: isDarkMode ? '0 12px 48px rgba(0,0,0,0.5)' : '0 12px 48px rgba(0,0,0,0.2)',
            maxWidth: '420px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            position: 'relative',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => setShowFullProfileModal(false)}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              backgroundColor: colors.backgroundDark,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '22px',
              color: colors.textSecondary,
              zIndex: 1,
            }}
          >
            ×
          </button>

          <div style={{ padding: '24px 20px 20px' }}>
            {/* Header: pic, name, type, city, status */}
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={profilePic}
                  alt={displayName}
                  style={{
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    border: `4px solid ${colors.surface}`,
                    boxShadow: isDarkMode ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.15)',
                    objectFit: 'cover',
                  }}
                />
                <div style={{
                  position: 'absolute',
                  bottom: '6px',
                  right: '6px',
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: userData?.isOnline ? '#4CAF50' : '#9E9E9E',
                  border: `3px solid ${colors.surface}`,
                }} />
              </div>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '6px 12px',
                borderRadius: '16px',
                backgroundColor: userData?.accountType === 'CLUB' ? '#76AF87' : colors.primary,
                marginTop: '-14px',
                fontSize: '11px',
                fontWeight: '600',
                color: 'white',
              }}>
                {userData?.accountType === 'CLUB' ? '🏛️ Club' : '♟️ Player'}
              </div>
            </div>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: colors.text, marginBottom: '4px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>{displayName}</div>
            {userData?.city && (
              <div style={{ fontSize: '12px', color: colors.textSecondary, marginBottom: '6px' }}>📍 {userData.city}</div>
            )}
            {userData?.statusMessage && (
              <div style={{ fontSize: '12px', color: colors.textSecondary, fontStyle: 'italic', marginBottom: '12px' }}>"{userData.statusMessage}"</div>
            )}

            {/* Player: stats */}
            {userData?.accountType === 'PLAYER' && (
              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', padding: '12px', backgroundColor: colors.backgroundDark, borderRadius: '10px' }}>
                {userData?.rating != null && (
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text }}>{userData.rating}</div>
                    <div style={{ fontSize: '10px', color: colors.textSecondary }}>Rating</div>
                  </div>
                )}
                {userData?.gamesPlayedInApp != null && (
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text }}>{userData.gamesPlayedInApp || 0}</div>
                    <div style={{ fontSize: '10px', color: colors.textSecondary }}>Games</div>
                  </div>
                )}
              </div>
            )}

            {/* Club: about, address, website, members */}
            {userData?.accountType === 'CLUB' && (
              <>
                {userData?.about && (
                  <div style={{ fontSize: '12px', color: colors.text, marginBottom: '10px', lineHeight: 1.4 }}>{userData.about}</div>
                )}
                {userData?.address && (
                  <div style={{ fontSize: '12px', color: colors.text, marginBottom: '8px' }}>📍 {userData.address}</div>
                )}
                {userData?.website && (
                  <a href={userData.website.startsWith('http') ? userData.website : `https://${userData.website}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: colors.primary, marginBottom: '8px', display: 'block' }}>🌐 Visit Website</a>
                )}
                <div style={{ fontSize: '12px', color: colors.textSecondary, marginBottom: '12px' }}>👥 {memberCount} {memberCount === 1 ? 'member' : 'members'}{isMember && ' · You\'re a member'}</div>
                {/* Join/Leave Club - only if not owner */}
                {currentUserDbId && userData?.claimedBy !== currentUserDbId && (
                  <button
                    type="button"
                    onClick={isMember ? handleLeaveClub : handleJoinClub}
                    disabled={membershipLoading}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      marginBottom: '8px',
                      borderRadius: '10px',
                      border: 'none',
                      backgroundColor: isMember ? '#757575' : colors.primary,
                      color: 'white',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: membershipLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {membershipLoading ? '...' : (isMember ? 'Leave Club' : 'Join Club')}
                  </button>
                )}
              </>
            )}

            {userData?.accountType === 'CLUB' && upcomingEvents.length > 0 && (
              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${colors.border}` }}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: colors.textSecondary, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  📅 Upcoming Events
                </div>
                {upcomingEvents.map((event) => {
                  const eventDate = new Date(event.eventDate);
                  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  const dayName = days[eventDate.getDay()];
                  const monthName = months[eventDate.getMonth()];
                  const timeStr = event.eventTime
                    ? (() => {
                        const [hours, minutes] = event.eventTime.split(':');
                        const hour = parseInt(hours);
                        const ampm = hour >= 12 ? 'pm' : 'am';
                        const displayHour = hour % 12 || 12;
                        return `${displayHour}${minutes !== '00' ? `:${minutes}` : ''}${ampm}`;
                      })()
                    : '';
                  const rsvpCount = event.rsvps?.length || event.rsvps?.count || 0;
                  return (
                    <div key={event.id} style={{ padding: '10px', backgroundColor: colors.backgroundDark, borderRadius: '8px', borderLeft: `3px solid ${colors.primary}`, marginBottom: '8px' }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: colors.text, marginBottom: '4px' }}>{event.title}</div>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '6px' }}>
                        {dayName}, {monthName} {eventDate.getDate()} {timeStr && `· ${timeStr}`} {rsvpCount > 0 && `· ${rsvpCount} RSVP${rsvpCount !== 1 ? 's' : ''}`}
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button type="button" onClick={() => { if (navigation) { navigation.navigate('EventDetails', { eventId: event.id }); setShowFullProfileModal(false); onClose(); } }}
                          style={{ flex: 1, padding: '7px', backgroundColor: colors.primary, border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: '600', color: 'white', cursor: 'pointer' }}>
                          View Event
                        </button>
                        {!event.userHasRSVP && currentUserDbId && (
                          <button type="button" onClick={() => handleRSVP(event)}
                            style={{ flex: 1, padding: '7px', backgroundColor: 'transparent', border: `1px solid ${colors.primary}`, borderRadius: '6px', fontSize: '11px', fontWeight: '600', color: colors.primary, cursor: 'pointer' }}>
                            RSVP
                          </button>
                        )}
                        {event.userHasRSVP && (
                          <div style={{ flex: 1, padding: '7px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: isDarkMode ? '#81C784' : '#4CAF50' }}>
                            ✓ RSVP'd
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add Friend - players only */}
            {renderFriendButton()}

            {/* Send Message */}
            <button
              type="button"
              onClick={() => {
                onSendMessage(userData);
                setShowFullProfileModal(false);
                onClose();
              }}
              style={{
                width: '100%',
                padding: '12px 20px',
                marginTop: '8px',
                borderRadius: '10px',
                border: 'none',
                backgroundColor: colors.primary,
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              💬 Send Message
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Payment Modal - must be outside OverlayView since OverlayView requires a single child */}
    {selectedEventForPayment && (
      <PaymentModal
        visible={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setSelectedEventForPayment(null);
        }}
        event={selectedEventForPayment}
        userId={currentUserDbId}
        onPaymentSuccess={handlePaymentSuccess}
      />
    )}
    </>
  );
};

export default UserProfilePopup;
